import { Component, Element, Host, h, Prop, State, Watch } from '@stencil/core';
import {
  ContentTeaserData,
  defaultFetchConfig,
} from '../../helpers/services/ContentTeaserData';
import type {
  FetchTeaserServiceConfig,
  ContentServiceResult,
} from '../../helpers/utils/interfaces';
import {
  checkLoadMoreContent,
  mapContentServiceResultToTeaserList,
  moreContentButtonClassMap,
} from '../../helpers/contentServiceComponent';
@Component({
  tag: 'cms-tag-page',
  styleUrl: 'cms-tag-page.scss',
  shadow: true,
})
export class CmsTagPage {
  private readonly loadDataConfig = {
    ...defaultFetchConfig,
    ...{
      locale: null,
      companyCode: null,
      responsePage: 1,
      responseTeaserCount: 18,
      tag: null,
    },
  } as FetchTeaserServiceConfig;
  private moreButton: HTMLButtonElement;

  private readonly imageSizes = `
  (min-width:768px) and (max-width: 1279px) 50vw,
  (min-width:1280px) 368px,
  100vw`;

  @State() teaserResult: ContentServiceResult | undefined;

  /**
   * Set the button label for load more Teaser
   */
  @Prop() moreTeaserLabel = '';

  /**
   * Set the label for load more Teaser at trimmed text
   */
  @Prop() moreTeaserButtonLabel = '';

  /**
   * accessible name for the "load more" button"
   */
  @Prop() moreAccessibleName: string | undefined;

  /**
   * Set the accessible-name for teasers (ignoring the accessible name provided by the teaser itself)
   */
  @Prop() teaserAccessibleName?: string;

  /**
   * set the user locale like de_DE
   */
  @Prop({ mutable: true, reflect: true }) locale: string | undefined;

  /**
   * set the user companyCode like 1001
   */
  @Prop({ mutable: true, reflect: true }) companyCode: string | undefined;

  /**
   * Set the filter uuid represent like bauen, wohnen etc.  to use in the query for request the content service
   */
  @Prop({ mutable: true, reflect: true }) filterId: string | undefined;

  /**
   * Set the Filter Type like Rubric ore Keyword to use in the query for request the content service
   */
  @Prop() filterType: string | undefined;

  /**
   * Set the tracking namespace (optional)
   */
  @Prop({ mutable: true }) namespace?: string;

  /**
   * ref to the host element
   * @type {HTMLElement}
   * @memberof CmsTagPage
   */
  @Element() host: HTMLElement | undefined;

  @Watch('locale')
  @Watch('companyCode')
  @Watch('filterId')
  watchFilter() {
    this.updateData();
  }

  private setState(newState: ContentServiceResult) {
    if (this.teaserResult !== newState) {
      this.teaserResult = newState;
    }
  }

  private async loadMoreContentController() {
    this.loadDataConfig.responsePage++;
    try {
      const moreTeaserResult: ContentServiceResult =
        await ContentTeaserData.getDataFromService(this.loadDataConfig);
      if (this.teaserResult && moreTeaserResult.caas?.documents) {
        moreTeaserResult.caas.documents = [
          ...this.teaserResult.caas.documents,
          ...moreTeaserResult.caas.documents,
        ];
      }
      this.setState(moreTeaserResult);
    } catch (error) {
      console.error(error);
    }
    if (!checkLoadMoreContent(this.teaserResult, this.loadDataConfig)) {
      this.hideMore();
    }
  }

  private async hideMore() {
    this.moreButton.style.display = 'none';
  }

  async componentDidLoad() {
    this.moreButton = this.host.shadowRoot.querySelector(
      '.load-more-teaser',
    ) as HTMLButtonElement;
    if (!checkLoadMoreContent(this.teaserResult, this.loadDataConfig)) {
      this.hideMore();
    }
  }

  async componentWillLoad(): Promise<void> {
    await this.updateData();
  }

  async updateData(): Promise<void> {
    if (this.locale === undefined || this.companyCode === null) {
      this.teaserResult = undefined;
      return;
    }

    Object.assign(this.loadDataConfig, {
      [this.filterType]: this.filterId,
      locale: this.locale,
      companyCode: this.companyCode,
    });

    const teaserResult = await ContentTeaserData.getDataFromService(
      this.loadDataConfig,
    );
    this.setState(teaserResult);
  }

  render() {
    return (
      <Host class={'dynamic-teasers'}>
        <div class={'teaser-list-container dynamic-teasers'}>
          <div class={'teaser-list-container-inner'}>
            {mapContentServiceResultToTeaserList(this.teaserResult)?.map(
              (teaser) => (
                <cms-teaser
                  teaser={teaser}
                  imageSizes={this.imageSizes}
                  moreInfoLabel={this.moreTeaserLabel}
                  reducedMargins={true}
                  teaserAccessibleName={this.teaserAccessibleName}
                />
              ),
            )}
          </div>
        </div>

        <div
          class={moreContentButtonClassMap(
            this.teaserResult,
            this.loadDataConfig,
          )}
        >
          <cms-button
            namespace={`${this.namespace}-more`}
            type={'button'}
            size={'button-md'}
            theme={'secondary'}
            label={this.moreTeaserButtonLabel}
            class={'load-more-teaser'}
            accessibleName={this.moreAccessibleName}
            onCmsButtonClick={async () => {
              await this.loadMoreContentController();
            }}
          />
        </div>
      </Host>
    );
  }
}
